import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { useState } from 'react';
import { Eye } from 'lucide-react';
import { Button } from '../../../components/ui';
import { useReleaseResults, useUnreleaseResults } from '../../../api/hooks';
import type { ResultsReleaseState } from '../../../api/types';
import { withTime } from '../../../lib/dates';

export default function ResultsReleaseCard({ examId, report, dueAt, awaitingCount = 0 }: {
  examId: number; report: ResultsReleaseState; dueAt?: string | null;
  /** Results held back until their written answers are marked. */
  awaitingCount?: number;
}) {
  const { role } = useAuth();
  const canWrite = role === 'school_admin' || role === 'school' || role === 'admin' || role === 'teacher';
  const release = useReleaseResults(examId);
  const unrelease = useUnreleaseResults(examId);
  const [error, setError] = useState('');
  const { results_release: mode, released } = report;

  const fail = (e: any) => setError(e?.response?.data?.detail ?? 'Could not update the release. Try again.');

  let state: string;
  if (mode === 'immediate' || released) state = 'Visible to students';
  else if (mode === 'after_due') state = `Hidden until ${dueAt ? withTime(dueAt) : 'the due time'}`;
  else state = 'Hidden';
  const visible = mode === 'immediate' || !!released;
  // No due date: the correct answers wait for the teacher's release in every mode
  // (the score still follows the release mode).
  const answersOnly = mode !== 'manual' && dueAt === null;
  const answersReleased = !!report.results_released_at;
  const releasedDay = () => {
    const when = report.results_released_at ? new Date(report.results_released_at) : null;
    return when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null;
  };
  let note: string;
  if (answersOnly && answersReleased) {
    const day = releasedDay();
    note = `${day ? `Answers released ${day}.` : 'Answers released.'} Students can see their score and the correct answers.`;
  }
  else if (answersOnly) note = 'Students see their score as soon as they submit. The correct answers open when you release them.';
  else if (mode === 'immediate' && dueAt && !(new Date(dueAt).getTime() <= Date.now())) {
    note = 'Students see their score as soon as they submit. The correct answers open after the due time.';
  }
  else if (mode === 'immediate') note = 'Students see their score and answers as soon as they submit.';
  else if (mode === 'after_due') note = 'Shown to students automatically once the due time has passed.';
  else if (released) {
    const day = releasedDay();
    note = `${day ? `Released ${day}.` : 'Released.'} Students can see their score and the correct answers.`;
  }
  else note = 'Release mode is manual. Students see nothing until you release.';

  return (
    <section aria-label="Results for students" className="rounded-[20px] border border-line bg-surface px-[22px] py-[18px] shadow-card">
      <div className="flex flex-wrap items-center gap-4">
        <span aria-hidden="true" className={`inline-grid h-11 w-11 flex-none place-items-center rounded-[14px] ${visible ? 'bg-success-bg text-success' : 'bg-mist text-slate'}`}>
          <Eye size={22} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1 basis-[260px]">
          <h3 className="font-semibold text-ink">Results for students · <span>{state}</span></h3>
          <p className="mt-0.5 text-[13px] text-muted">{note}</p>
          {awaitingCount > 0 && (
            <p className="mt-1 text-sm text-warning-ink">
              {awaitingCount === 1
                ? '1 result stays hidden until its written answers are marked.'
                : `${awaitingCount} results stay hidden until their written answers are marked.`}
            </p>
          )}
        </div>
        {canWrite && mode === 'manual' && !released && (
          <Button variant="secondary" size="md" loading={release.isPending} disabled={release.isPending} onClick={() => { setError(''); release.mutate(undefined, { onError: fail }); }}>Release results</Button>
        )}
        {canWrite && mode === 'manual' && released && (
          <Button variant="ghost" size="md" loading={unrelease.isPending} disabled={unrelease.isPending} onClick={() => { setError(''); unrelease.mutate(undefined, { onError: fail }); }}>Hide results</Button>
        )}
        {canWrite && answersOnly && !answersReleased && (
          <Button variant="secondary" size="md" loading={release.isPending} disabled={release.isPending} onClick={() => { setError(''); release.mutate(undefined, { onError: fail }); }}>Release answers</Button>
        )}
        {canWrite && answersOnly && answersReleased && (
          <Button variant="ghost" size="md" loading={unrelease.isPending} disabled={unrelease.isPending} onClick={() => { setError(''); unrelease.mutate(undefined, { onError: fail }); }}>Hide answers</Button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </section>
  );
}
